<template>
  <div class="location-list">
    <div
      v-for="{ region, venues } in venuesByRegion"
      :key="region"
      class="region"
      :class="{ active: isRegionVisible(region) }"
    >
      <sh-button-deprecated
        ghost
        class="dropdown-button"
        @click="toggleRegionVisiblity(region)"
      >
        {{ getRegionLabel(region) }}
        <icon class="icon" icon="disclosure" size="small" />
      </sh-button-deprecated>

      <div class="dropdown-container">
        <transition-group name="accordion-list">
          <component
            v-for="venue in venues"
            v-if="isRegionVisible(region)"
            v-model="selected"
            :is="componentType"
            :color="backgroundColor"
            :disabled="isDisabled(venue)"
            :icon="venue.icons.darkSvg"
            :key="venue.id"
            :value="venue.id"
            :name="venue.name"
          >
            {{ venue.name }}
          </component>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import designTokens from '@sohohouse/design-system/dist/tokens/tokens.json'
import CheckButton from '~/components/check-button'
import { getVenuesByRegion } from '~/helpers'

import { CONSTANTS } from '@sohohouse/platform-resources'

export default {
  components: {
    CheckButton
  },
  data () {
    return {
      regions: {},
      backgroundColor: designTokens.color_black
    }
  },
  props: {
    availableVenueIds: {
      type: Array
    },
    houses: {
      type: Array,
      required: true
    },
    citiesWithoutHouses: {
      type: Array,
      required: false,
      default: []
    },
    localHouse: {
      type: Object,
      required: false
    },
    value: {
      type: [String, Array],
      default: null
    },
    radio: {
      type: Boolean,
      default: false
    },
    disableLocal: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    selected: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
      }
    },
    componentType () {
      return this.radio ? 'radio-button' : 'check-button'
    },
    venuesByRegion () {
      return getVenuesByRegion([...this.houses, ...this.citiesWithoutHouses])
    }
  },
  mounted () {
    this.venues = [...this.houses, ...this.citiesWithoutHouses]
    this.regions = this.venues.reduce((acc, venue) => {
      const isCWH = venue.venueType === CONSTANTS.VENUE_TYPES.CWH
      const group = isCWH ? CONSTANTS.VENUE_TYPES.CWH : venue.region
      const label = isCWH ? this.$t('REGION_CWH') : this.$t(`REGION_LABEL_${venue.region}`)

      return {
        ...acc,
        [group]: {
          isVisible: false,
          label
        }
      }
    }, {})
  },
  methods: {
    isDisabled (venue) {
      return (this.disableLocal && venue.id === this.localHouse.id)
        || (this.availableVenueIds && !this.availableVenueIds.includes(venue.id))
    },

    toggleRegionVisiblity (region) {
      this.regions[region].isVisible = !this.regions[region].isVisible
    },

    isRegionVisible (region) {
      return this.regions[region] && this.regions[region].isVisible
    },

    getRegionLabel (region) {
      let count
      if (region === CONSTANTS.VENUE_TYPES.CWH) {
        count = this.citiesWithoutHouses.filter(city => {
          if (this.radio) {
            return this.selected = city.id
          }
          return this.selected.includes(city.id)
        }).length
      } else {
        count = this.houses.filter(thisHouse => {
          const isRightRegion = thisHouse.region === region
          let isSelected = false
          if (this.radio) {
            isSelected = this.selected === thisHouse.id
          } else {
            isSelected = this.selected.includes(thisHouse.id)
          }
          return isRightRegion && isSelected
        }).length
      }

      const label = this.regions[region] && this.regions[region].label
      if (label && count) {
        return `${label} • ${count}`
      } else if (label) {
        return label
      }

      return null
    }
  }
}
</script>

<style lang="scss" scoped>

.accordion-list-enter-active,
.accordion-list-leave-active {
  @include transition-fast;
}

.accordion-list-enter,
.accordion-list-leave-to {
  opacity: 0;
  margin-bottom: -64px;
}

.dropdown-button.ghost {
  @include font('060');
  display: flex;
  justify-content: space-between;
  align-content: center;
  margin-bottom: $space-050;
  padding: $space-040 0;
  width: 100%;
  z-index: 1;

  &:after {
    border-radius: 2px 2px 0 0;
    border: none;
    z-index: -1;
  }
}

.region {
  &.active {
    .dropdown-button .icon {
      transform: rotate(-180deg) translate3d(0, 1px, 0);
    }
  }
}

</style>
